import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";

// Layered monthly timeline. Stacks actuals (past), booked pipeline,
// estimate pipeline, baseline, and adjustments per month into a single
// bar; overlays prior-year as a dashed line. The stack reads as a
// certainty gradient: actuals (invoiced) → booked pipeline
// (booked-not-yet-invoiced) → estimate pipeline (open estimates ×
// win-rate) → baseline forecast. Booked pipeline is invoiced-view only.
// Used at company / rep / bucket levels of the Plan workspace. Built on
// Recharts ComposedChart.

export interface LayeredTimelineChartProps {
  monthLabels: string[];
  baseline: number[];
  adjustment: number[];
  pipeline: number[];
  /** Booked-but-not-yet-invoiced work by expected invoice month.
   *  Invoiced-view only — omit/empty on the booked view. Renders as the
   *  certainty tier between actuals and estimate pipeline. */
  bookedPipeline?: number[];
  actuals: number[];
  /** Optional prior-year overlay line — same 12 months, -12 month shift. */
  priorYear?: number[];
  height?: "sm" | "md" | "lg";
  title?: string;
  /**
   * Index of the bar that corresponds to the cycle's as-of date.
   * When set, draws a labelled vertical reference line between bars.
   * Use on retrospective surfaces only — irrelevant on /plan.
   */
  asOfIndex?: number;
  /**
   * Index of the bar that corresponds to "today". When set, draws a
   * second labelled vertical line. Use on retrospective surfaces to
   * mark the comparison/forecast boundary.
   */
  todayIndex?: number;
}

const HEIGHT_PX: Record<
  NonNullable<LayeredTimelineChartProps["height"]>,
  number
> = {
  sm: 128,
  md: 160,
  lg: 192,
};

// Stable hex equivalents of the pink/amber Tailwind utilities the old
// chart used, so visual continuity holds across the migration.
const COLOUR = {
  actuals: "#be185d", // pink-700
  bookedPipeline: "#ec4899", // pink-500
  pipeline: "#f9a8d4", // pink-300 (estimate pipeline)
  baseline: "#fbcfe8", // pink-200
  adjustmentPos: "#fbbf24", // amber-400
  ghostBorder: "#a3a3a3", // neutral-400
  priorYear: "#525252", // neutral-600
  axis: "#737373",
  grid: "#e5e5e5",
};

function fmtK(n: number): string {
  if (!n) return "£0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}k`;
  return `${sign}£${Math.round(abs)}`;
}

interface ChartDatum {
  month: string;
  // Stack components — sum to the bar's visual height.
  actuals: number;
  bookedPipeline: number;
  pipeline: number;
  baselineNet: number;
  baselineGhost: number;
  adjustmentPos: number;
  // Original values used for the tooltip (so we show "Baseline £88k"
  // and "Adjustment +£12k" rather than the post-split layers).
  baselineOriginal: number;
  adjustmentSigned: number;
  priorYear: number | null;
}

function buildData(
  monthLabels: string[],
  baseline: number[],
  adjustment: number[],
  pipeline: number[],
  bookedPipeline: number[] | undefined,
  actuals: number[],
  priorYear: number[] | undefined,
): ChartDatum[] {
  const N = monthLabels.length || 12;
  const pad = (arr: number[]): number[] =>
    arr.length >= N
      ? arr.slice(0, N)
      : [...arr, ...Array(N - arr.length).fill(0)];
  const b = pad(baseline);
  const a = pad(adjustment);
  const p = pad(pipeline);
  const bp = pad(bookedPipeline ?? []);
  const ac = pad(actuals);
  const py = priorYear ? pad(priorYear) : null;
  return monthLabels.slice(0, N).map((m, i) => {
    // Negative adjustments eat into the baseline; the eaten portion
    // stays in the bar as a striped ghost so the user can see what was
    // cut. Positive adjustments stack on top as amber.
    const adjPos = Math.max(0, a[i]);
    const adjNeg = Math.min(0, a[i]); // ≤ 0
    const baselineNet = Math.max(0, b[i] + adjNeg);
    const baselineGhost = Math.min(b[i], -adjNeg);
    return {
      month: m,
      actuals: ac[i],
      bookedPipeline: bp[i],
      pipeline: p[i],
      baselineNet,
      baselineGhost,
      adjustmentPos: adjPos,
      baselineOriginal: b[i],
      adjustmentSigned: a[i],
      priorYear: py ? py[i] : null,
    };
  });
}

export function LayeredTimelineChart({
  monthLabels,
  baseline,
  adjustment,
  pipeline,
  bookedPipeline,
  actuals,
  priorYear,
  height = "md",
  title,
  asOfIndex,
  todayIndex,
}: LayeredTimelineChartProps) {
  const data = buildData(
    monthLabels,
    baseline,
    adjustment,
    pipeline,
    bookedPipeline,
    actuals,
    priorYear,
  );
  const hasPY = priorYear && priorYear.some((v) => v > 0);
  const hasBookedPipeline =
    !!bookedPipeline && bookedPipeline.some((v) => v > 0);

  return (
    <div className="space-y-2">
      {title && <h3 className="text-sm font-semibold">{title}</h3>}
      <ResponsiveContainer width="100%" height={HEIGHT_PX[height]}>
        <ComposedChart
          data={data}
          margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
        >
          {/* Diagonal-stripes fill for the cut-baseline ghost. */}
          <defs>
            <pattern
              id="ghostPattern"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(135)"
            >
              <rect width="6" height="6" fill="transparent" />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="6"
                stroke="rgba(120,120,120,0.35)"
                strokeWidth="2.5"
              />
            </pattern>
          </defs>
          <CartesianGrid stroke={COLOUR.grid} vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: COLOUR.axis }}
            axisLine={{ stroke: COLOUR.grid }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={fmtK}
            tick={{ fontSize: 10, fill: COLOUR.axis }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip content={LayeredTooltip as never} />
          <Bar
            dataKey="actuals"
            stackId="bar"
            fill={COLOUR.actuals}
            isAnimationActive={false}
          />
          {hasBookedPipeline && (
            <Bar
              dataKey="bookedPipeline"
              stackId="bar"
              fill={COLOUR.bookedPipeline}
              isAnimationActive={false}
            />
          )}
          <Bar
            dataKey="pipeline"
            stackId="bar"
            fill={COLOUR.pipeline}
            isAnimationActive={false}
          />
          <Bar
            dataKey="baselineNet"
            stackId="bar"
            fill={COLOUR.baseline}
            isAnimationActive={false}
          />
          <Bar
            dataKey="baselineGhost"
            stackId="bar"
            fill="url(#ghostPattern)"
            stroke={COLOUR.ghostBorder}
            strokeDasharray="2 2"
            strokeWidth={1}
            isAnimationActive={false}
          />
          <Bar
            dataKey="adjustmentPos"
            stackId="bar"
            fill={COLOUR.adjustmentPos}
            isAnimationActive={false}
          />
          {hasPY && (
            <Line
              dataKey="priorYear"
              type="monotone"
              stroke={COLOUR.priorYear}
              strokeWidth={1.4}
              strokeDasharray="4 3"
              dot={{ r: 2, fill: COLOUR.priorYear }}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          )}
          {typeof asOfIndex === "number" &&
            asOfIndex >= 0 &&
            asOfIndex < data.length && (
              <ReferenceLine
                x={data[asOfIndex].month}
                stroke="#7c3aed"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{
                  value: "As-of",
                  position: "top",
                  fill: "#7c3aed",
                  fontSize: 10,
                }}
              />
            )}
          {typeof todayIndex === "number" &&
            todayIndex >= 0 &&
            todayIndex < data.length && (
              <ReferenceLine
                x={data[todayIndex].month}
                stroke="#0891b2"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{
                  value: "Today",
                  position: "top",
                  fill: "#0891b2",
                  fontSize: 10,
                }}
              />
            )}
          <Legend
            content={
              <LayeredLegend
                showBookedPipeline={hasBookedPipeline}
                showPriorYear={!!hasPY}
                showAsOfMarker={
                  typeof asOfIndex === "number" &&
                  asOfIndex >= 0 &&
                  asOfIndex < data.length
                }
                showTodayMarker={
                  typeof todayIndex === "number" &&
                  todayIndex >= 0 &&
                  todayIndex < data.length
                }
              />
            }
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Custom tooltip ──────────────────────────────────────────────────

function LayeredTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0)
    return null;
  const datum = props.payload[0]?.payload as ChartDatum | undefined;
  if (!datum) return null;
  const total =
    Math.max(datum.baselineOriginal, datum.baselineNet) +
    datum.adjustmentPos +
    datum.pipeline +
    datum.bookedPipeline +
    datum.actuals;
  return (
    <div
      className="rounded border border-border bg-popover text-popover-foreground px-2 py-1 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="font-semibold">{datum.month}</div>
      <TooltipRow
        swatch={COLOUR.actuals}
        label="Actuals"
        value={fmtK(datum.actuals)}
      />
      {datum.bookedPipeline > 0 && (
        <TooltipRow
          swatch={COLOUR.bookedPipeline}
          label="Booked pipeline"
          value={fmtK(datum.bookedPipeline)}
        />
      )}
      <TooltipRow
        swatch={COLOUR.pipeline}
        label="Estimate pipeline"
        value={fmtK(datum.pipeline)}
      />
      <TooltipRow
        swatch={COLOUR.baseline}
        label="Forecast"
        value={fmtK(datum.baselineOriginal)}
      />
      <TooltipRow
        swatch={COLOUR.adjustmentPos}
        label="Adjustment"
        value={
          (datum.adjustmentSigned >= 0 ? "+" : "") +
          fmtK(datum.adjustmentSigned)
        }
      />
      {datum.priorYear !== null && (
        <TooltipRow
          swatch={COLOUR.priorYear}
          label="Prior year"
          value={fmtK(datum.priorYear)}
        />
      )}
      <div className="mt-1 border-t border-border pt-1 font-semibold">
        Total: {fmtK(total)}
      </div>
    </div>
  );
}

function TooltipRow({
  swatch,
  label,
  value,
}: {
  swatch: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <span
        className="inline-block w-2 h-2 rounded-sm"
        style={{ backgroundColor: swatch }}
      />
      <span>
        {label}: {value}
      </span>
    </div>
  );
}

// ── Custom legend ──────────────────────────────────────────────────

function LayeredLegend({
  showBookedPipeline,
  showPriorYear,
  showAsOfMarker,
  showTodayMarker,
}: {
  showBookedPipeline?: boolean;
  showPriorYear: boolean;
  showAsOfMarker?: boolean;
  showTodayMarker?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
      <LegendSwatch fill={COLOUR.actuals} label="Actuals" />
      {showBookedPipeline && (
        <LegendSwatch fill={COLOUR.bookedPipeline} label="Booked pipeline" />
      )}
      <LegendSwatch fill={COLOUR.pipeline} label="Estimate pipeline" />
      <LegendSwatch fill={COLOUR.baseline} label="Forecast" />
      <LegendSwatch fill={COLOUR.adjustmentPos} label="Adjustment" />
      <LegendSwatch
        label="Cut by adjustment"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(120,120,120,0.35) 0 2px, transparent 2px 6px)",
          border: "1px dashed rgba(120,120,120,0.6)",
        }}
      />
      {showPriorYear && (
        <DashedLineLegend stroke="#525252" label="Prior year" />
      )}
      {showAsOfMarker && (
        <DashedLineLegend stroke="#7c3aed" label="As-of (cycle close)" />
      )}
      {showTodayMarker && <DashedLineLegend stroke="#0891b2" label="Today" />}
    </div>
  );
}

function DashedLineLegend({
  stroke,
  label,
}: {
  stroke: string;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <svg width="14" height="6" viewBox="0 0 14 6" aria-hidden="true">
        <line
          x1="0"
          y1="3"
          x2="14"
          y2="3"
          stroke={stroke}
          strokeWidth="1.4"
          strokeDasharray="3 2"
        />
      </svg>
      <span>{label}</span>
    </span>
  );
}

function LegendSwatch({
  fill,
  label,
  style,
}: {
  fill?: string;
  label: string;
  style?: React.CSSProperties;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block w-3 h-3 rounded-sm"
        style={{ backgroundColor: fill, ...style }}
      />
      {label}
    </span>
  );
}
